// Reads and writes for the agents area (spec 14 §5, the rows 14-A owns, D-512
// ruling 2), all through the generated client. Every queryFn passes its
// signal; every write carries a fresh Idempotency-Key.

import { useQuery } from "@tanstack/react-query";
import { client } from "../../../../lib/api";
import { shouldRetryRead } from "../../../../lib/query";
import { RequestError, newIdempotencyKey, unwrap, unwrapVoid } from "../../../../lib/request";
import type {
  ActionStatus,
  AgentAction,
  AgentDetail,
  AgentListItem,
  AgentPolicyItem,
  GuardrailKind,
  JournalSort,
} from "../lib/agents-lib";
import type { components } from "@assessly/api-client";

type S = components["schemas"];

const LIVE = { retry: shouldRetryRead, staleTime: 30_000 } as const;
const REF = { retry: shouldRetryRead, staleTime: 5 * 60_000, gcTime: 30 * 60_000 } as const;

export const agentsKeys = {
  all: ["agents"] as const,
  list: () => ["agents", "list"] as const,
  detail: (id: string) => ["agents", "detail", id] as const,
  policies: (id: string) => ["agents", "policies", id] as const,
  promptVersions: (id: string) => ["agents", "prompt-versions", id] as const,
  tools: () => ["agents", "tools"] as const,
  settings: () => ["agents", "settings"] as const,
  actions: () => ["agents", "actions"] as const,
  actionPage: (query: ActionListQuery, cursor: string | undefined) =>
    ["agents", "actions", "page", query, cursor ?? null] as const,
  action: (id: string) => ["agents", "actions", "one", id] as const,
  guardrails: () => ["agents", "guardrails"] as const,
  guardrailPage: (query: GuardrailQuery, cursor: string | undefined) =>
    ["agents", "guardrails", "page", query, cursor ?? null] as const,
  guardrailBanner: (from: string) => ["agents", "guardrails", "banner", from] as const,
  run: (id: string) => ["agents", "run", id] as const,
  messageRuns: (agentId: string, messageId: string) =>
    ["agents", "runs", "message", agentId, messageId] as const,
  facilities: () => ["agents", "ref", "facilities"] as const,
  departments: (facilityId: string) => ["agents", "ref", "departments", facilityId] as const,
};

/** The problem code a failed call carried, if any. */
export function problemCode(error: unknown): string | undefined {
  return error instanceof RequestError ? error.code : undefined;
}

export function httpStatusOf(error: unknown): number | undefined {
  return error instanceof RequestError ? error.httpStatus : undefined;
}

// ---- agents ----------------------------------------------------------------

/** `GET /v1/agents`: every agent the reader may see (the invoke-only reader's own domain's). */
export function useAgents(enabled = true) {
  return useQuery({
    ...LIVE,
    enabled,
    queryKey: agentsKeys.list(),
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/agents", { signal }), "agents").items,
  });
}

export function useAgent(id: string) {
  return useQuery({
    ...LIVE,
    queryKey: agentsKeys.detail(id),
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/agents/{id}", { signal, params: { path: { id } } }), "agent"),
  });
}

export async function patchAgent(id: string, body: S["AgentPatchRequest"]): Promise<AgentDetail> {
  return unwrap(
    await client.PATCH("/v1/agents/{id}", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body,
    }),
    "agent",
  );
}

export async function putScopeBindings(
  id: string,
  bindings: S["AgentScopeBindingsRequest"]["bindings"],
): Promise<S["AgentScopeBindingsResponse"]> {
  return unwrap(
    await client.PUT("/v1/agents/{id}/scope-bindings", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body: { bindings },
    }),
    "scope bindings",
  );
}

// ---- tools and policies ----------------------------------------------------

export function useAgentTools(enabled = true) {
  return useQuery({
    ...REF,
    enabled,
    queryKey: agentsKeys.tools(),
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/agents/tools", { signal }), "agent tools").items,
  });
}

export function useAgentPolicies(id: string, enabled = true) {
  return useQuery({
    ...LIVE,
    enabled,
    queryKey: agentsKeys.policies(id),
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/agents/{id}/policies", { signal, params: { path: { id } } }),
        "policies",
      ).items,
  });
}

export async function putPolicy(
  id: string,
  toolCode: string,
  body: S["AgentPolicyPutRequest"],
): Promise<S["AgentPolicyRow"]> {
  return unwrap(
    await client.PUT("/v1/agents/{id}/policies/{toolCode}", {
      params: {
        path: { id, toolCode },
        header: { "Idempotency-Key": newIdempotencyKey() },
      },
      body,
    }),
    "policy",
  );
}

export async function dismissPolicy(id: string, toolCode: string): Promise<void> {
  unwrapVoid(
    await client.DELETE("/v1/agents/{id}/policies/{toolCode}", {
      params: {
        path: { id, toolCode },
        header: { "Idempotency-Key": newIdempotencyKey() },
      },
    }),
    "policy",
  );
}

// ---- settings --------------------------------------------------------------

export function useAgentSettings(enabled = true) {
  return useQuery({
    ...REF,
    enabled,
    queryKey: agentsKeys.settings(),
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/agents/settings", { signal }), "agent settings"),
  });
}

export async function patchAgentSettings(
  body: S["AgentSettingsPatchRequest"],
): Promise<S["AgentSettings"]> {
  return unwrap(
    await client.PATCH("/v1/agents/settings", {
      params: { header: { "Idempotency-Key": newIdempotencyKey() } },
      body,
    }),
    "agent settings",
  );
}

// ---- prompt versions (D-512 ruling 6, D-536 ruling 7) ------------------------

export function usePromptVersions(id: string, enabled = true) {
  return useQuery({
    ...LIVE,
    enabled,
    queryKey: agentsKeys.promptVersions(id),
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/agents/{id}/prompt-versions", { signal, params: { path: { id } } }),
        "prompt versions",
      ).items,
  });
}

export async function createPromptVersion(
  id: string,
  body: S["AgentPromptVersionCreateRequest"],
): Promise<S["AgentPromptVersion"]> {
  return unwrap(
    await client.POST("/v1/agents/{id}/prompt-versions", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body,
    }),
    "prompt version",
  );
}

export async function activatePromptVersion(id: string, versionId: string): Promise<void> {
  unwrap(
    await client.POST("/v1/agents/{id}/prompt-versions/{versionId}/activate", {
      params: {
        path: { id, versionId },
        header: { "Idempotency-Key": newIdempotencyKey() },
      },
    }),
    "prompt activation",
  );
}

// ---- the journal (spec 14 §4.4) ----------------------------------------------

export interface ActionListQuery {
  readonly status: ActionStatus;
  readonly needs_my_decision?: "true" | undefined;
  readonly agent_id?: string | undefined;
  readonly tool_code?: string | undefined;
  readonly facility_id?: string | undefined;
  readonly department_id?: string | undefined;
  readonly impact_min_minor?: number | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly sort?: JournalSort | undefined;
}

/** One page of cards. Undefined keys are left out, never sent empty. */
export function useActionPage(query: ActionListQuery, cursor: string | undefined) {
  return useQuery({
    ...LIVE,
    queryKey: agentsKeys.actionPage(query, cursor),
    queryFn: async ({ signal }) => {
      const params: Record<string, string | number> = { limit: 25 };
      for (const [key, value] of Object.entries(query)) {
        if (value !== undefined) params[key] = value as string | number;
      }
      if (cursor !== undefined) params["cursor"] = cursor;
      return unwrap(
        await client.GET("/v1/agent-actions", {
          signal,
          params: { query: params as never },
        }),
        "agent actions",
      );
    },
  });
}

export function useAction(id: string | undefined) {
  return useQuery({
    ...LIVE,
    enabled: id !== undefined,
    queryKey: agentsKeys.action(id ?? "none"),
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/agent-actions/{id}", {
          signal,
          params: { path: { id: id ?? "" } },
        }),
        "agent action",
      ),
  });
}

export async function approveAction(id: string, note?: string): Promise<AgentAction> {
  return unwrap(
    await client.POST("/v1/agent-actions/{id}/approve", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body: note === undefined ? {} : { note },
    }),
    "approve",
  );
}

export async function rejectAction(id: string, note?: string): Promise<AgentAction> {
  return unwrap(
    await client.POST("/v1/agent-actions/{id}/reject", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body: note === undefined ? {} : { note },
    }),
    "reject",
  );
}

export async function undoAction(id: string, note?: string): Promise<S["AgentActionUndoResponse"]> {
  return unwrap(
    await client.POST("/v1/agent-actions/{id}/undo", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body: note === undefined ? {} : { note },
    }),
    "undo",
  );
}

// ---- guardrails and runs (agents.activity.read) -------------------------------

export interface GuardrailQuery {
  readonly kind?: GuardrailKind | undefined;
  readonly agent_id?: string | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
}

export function useGuardrailPage(
  query: GuardrailQuery,
  cursor: string | undefined,
  enabled = true,
) {
  return useQuery({
    ...LIVE,
    enabled,
    queryKey: agentsKeys.guardrailPage(query, cursor),
    queryFn: async ({ signal }) => {
      const params: Record<string, string | number> = { limit: 25 };
      for (const [key, value] of Object.entries(query)) {
        if (value !== undefined) params[key] = value as string;
      }
      if (cursor !== undefined) params["cursor"] = cursor;
      return unwrap(
        await client.GET("/v1/agent-guardrail-events", {
          signal,
          params: { query: params as never },
        }),
        "guardrail events",
      );
    },
  });
}

/**
 * The 24 h banner (spec 14 §4.2): one read per banner kind, since the list
 * filters on one kind at a time. `from` is fixed per mount so the key holds.
 */
export function useGuardrailBanner(
  from: string,
  kinds: readonly GuardrailKind[],
  enabled: boolean,
) {
  return useQuery({
    ...LIVE,
    enabled,
    queryKey: [...agentsKeys.guardrailBanner(from), kinds] as const,
    queryFn: async ({ signal }) => {
      const pages = await Promise.all(
        kinds.map(async (kind) =>
          unwrap(
            await client.GET("/v1/agent-guardrail-events", {
              signal,
              params: { query: { kind, from, limit: 100 } },
            }),
            "guardrail events",
          ),
        ),
      );
      return pages.flatMap((page) => page.items);
    },
  });
}

export function useRun(id: string | null, enabled: boolean) {
  return useQuery({
    ...LIVE,
    enabled: enabled && id !== null,
    queryKey: agentsKeys.run(id ?? "none"),
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/agent-runs/{id}", { signal, params: { path: { id: id ?? "" } } }),
        "agent run",
      ),
  });
}

// ---- Draft offer: a manual run on one message (D-515 ruling 4) ---------------

/** A run still working: the thread polls it and offers no second Draft offer. */
export function runInFlight(status: S["AgentRun"]["status"]): boolean {
  return status === "queued" || status === "running";
}

/** How often a thread re-reads a run in flight (the runs list is cheap). */
export const RUN_POLL_MS = 4_000;

/**
 * `POST /v1/agents/:id/trigger` with `{kind: "message", message_id}`. The
 * server answers it under the caller's own reach (D-519 ruling 5), so a
 * message the caller cannot see is refused, never laundered through the agent.
 */
export async function triggerOnMessage(
  agentId: string,
  messageId: string,
): Promise<S["AgentTriggerResponse"]> {
  return unwrap(
    await client.POST("/v1/agents/{id}/trigger", {
      params: { path: { id: agentId }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body: { trigger_ref: { kind: "message", message_id: messageId } },
    }),
    "agent trigger",
  );
}

/** `GET /v1/agent-runs/:id` once, outside a query (the expired card's Draft offer). */
export async function fetchRun(id: string): Promise<S["AgentRunDetail"]> {
  return unwrap(await client.GET("/v1/agent-runs/{id}", { params: { path: { id } } }), "agent run");
}

/**
 * The newest run of one agent on one message, or null when it has none. A
 * holder of `agents.invoke` reads only the runs whose subjects it reaches
 * (spec 14 §5), so a run on a customer the reader cannot see reads as none.
 * It polls while that run is queued or running.
 */
export function useMessageRun(agentId: string | undefined, messageId: string | undefined) {
  return useQuery({
    ...LIVE,
    enabled: agentId !== undefined && messageId !== undefined,
    queryKey: agentsKeys.messageRuns(agentId ?? "none", messageId ?? "none"),
    queryFn: async ({ signal }) => {
      const page = unwrap(
        await client.GET("/v1/agent-runs", {
          signal,
          params: {
            query: { agent_id: agentId ?? "", message_id: messageId ?? "", limit: 1 },
          },
        }),
        "agent runs",
      );
      return page.items[0] ?? null;
    },
    refetchInterval: (query) => {
      const run = query.state.data;
      return run !== undefined && run !== null && runInFlight(run.status) ? RUN_POLL_MS : false;
    },
  });
}

// ---- reference reads ---------------------------------------------------------

export function useAgentFacilities(enabled = true) {
  return useQuery({
    ...REF,
    enabled,
    queryKey: agentsKeys.facilities(),
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/org/facilities", { signal, params: { query: { status: "active" } } }),
        "facilities",
      ).items,
  });
}

export function useAgentDepartments(facilityId: string | undefined) {
  return useQuery({
    ...REF,
    enabled: facilityId !== undefined,
    queryKey: agentsKeys.departments(facilityId ?? "none"),
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/org/facilities/{id}/departments", {
          signal,
          params: { path: { id: facilityId ?? "" } },
        }),
        "departments",
      ).items,
  });
}

// ---- the office nav (D-529 ruling 1) -------------------------------------------

/**
 * Whether the office nav carries an Agents section: only when the reader may
 * call `GET /v1/agents` and it answers at least one row. A reader who may not
 * call it, a refusal and a failed read all render nothing (the copy budget's
 * rule, `tools/ci/copy-budget.ts`). `settled` lets the shell paint once.
 */
export function useAgentsNav(
  canList: boolean,
  feedResolved: boolean,
): {
  readonly visible: boolean;
  readonly settled: boolean;
} {
  const enabled = canList && feedResolved;
  const query = useAgents(enabled);
  const rows: readonly AgentListItem[] | undefined = query.data;
  return {
    visible: enabled && rows !== undefined && rows.length > 0,
    settled: !enabled || query.isFetched || query.isError,
  };
}

export type { AgentPolicyItem };
